import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Vault, Download, AlertTriangle, Clock, Shield, ChevronDown, Lock,
  FileText, FileSpreadsheet, FileSignature, Image as ImageIcon, Map as MapIcon, Files, Package,
} from "lucide-react";
import { drInvestorView, drInvestorDownloadUrl } from "../lib/api";
import { useBulkZip } from "../components/dataroom/BulkTooLargeModal";

// Kategorie-Icon basierend auf dem Phase-6.1 Schlüssel-Schema
const KATEGORIE_ICON = {
  "00_Datenraum-Index":         FileText,
  "01_Gesellschaft":            FileText,
  "02_Grundstueck_Eigentum":    FileSignature,
  "03_Baurecht_Genehmigungen":  FileSignature,
  "04_Plaene_Grundrisse":       MapIcon,
  "05_Stellplatznachweise":     MapIcon,
  "06_Mietvertraege":           FileSignature,
  "07_Behoerdenauskuenfte":     FileText,
  "08_Vertraege_Vereinbarungen": FileSignature,
  "09_Steuern":                 FileSpreadsheet,
  "10_Finanzierung":            FileSpreadsheet,
  "11_Wertgutachten":           FileSpreadsheet,
  "12_Betriebsvorrichtungen":   Files,
  "99_Intern":                  Lock,
  // Legacy
  "Exposé": FileText, "IM": FileText, "Rent Roll": FileSpreadsheet,
  "Cashflow": FileSpreadsheet, "Plan": MapIcon, "Foto": ImageIcon, "Vertrag": FileSignature,
};

const VISIBILITY_LABEL = {
  sofort:         { de: "Sofort verfügbar",        bg: "bg-heiss/10 text-heiss border-heiss/30" },
  nach_NDA:       { de: "Nach NDA",                bg: "bg-gold/10 text-gold border-gold/30" },
  nach_indikativ: { de: "Nach indikativem Gebot",  bg: "bg-tot/10 text-tot border-tot/30" },
  intern:         { de: "Intern",                  bg: "bg-navy/10 text-navy border-navy/30" },
};

const fmtSize = (b) => {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const drCategoryZipUrl = (token, kategorie) => {
  const backend = process.env.REACT_APP_BACKEND_URL;
  return `${backend}/api/datenraum/${encodeURIComponent(token)}/categories/${encodeURIComponent(kategorie)}/download-zip`;
};
const drAllZipUrl = (token) => {
  const backend = process.env.REACT_APP_BACKEND_URL;
  return `${backend}/api/datenraum/${encodeURIComponent(token)}/download-all-zip`;
};

export default function DatenraumInvestor() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const { triggerZip, modal: bulkModal } = useBulkZip();

  useEffect(() => {
    let cancelled = false;
    drInvestorView(token)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 410) setError("Dieser Datenraum-Link ist abgelaufen oder wurde deaktiviert.");
        else if (status === 404) setError("Dieser Datenraum-Link ist ungültig.");
        else if (status === 429) setError("Maximale Zugriffsanzahl erreicht.");
        else setError("Datenraum konnte nicht geladen werden.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  // Gruppiere Files + sortiere die Kategorien nach KATEGORIE_CONFIG-Order
  // Wichtig: Wir zeigen ALLE Kategorien (auch leere), damit Investor versteht
  // welche Inhalte nach NDA/Gebot zusätzlich verfügbar werden.
  const groupedSorted = useMemo(() => {
    if (!data) return [];
    const grouped = (data.files || []).reduce((acc, f) => {
      const k = f.kategorie || "Sonstiges";
      if (!acc[k]) acc[k] = [];
      acc[k].push(f);
      return acc;
    }, {});
    const fromBackend = (data.kategorien || []).filter((k) => k.key !== "99_Intern");
    const sorted = fromBackend.map((k) => ({
      kat: k.key,
      files: grouped[k.key] || [],
      meta: k,
    }));
    // Legacy: Files in Kategorien, die NICHT in der Backend-Config sind
    const backendKeys = new Set(fromBackend.map((k) => k.key));
    Object.keys(grouped).forEach((k) => {
      if (!backendKeys.has(k) && k !== "99_Intern") {
        sorted.push({ kat: k, files: grouped[k], meta: null });
      }
    });
    return sorted;
  }, [data]);

  // Default-Open: nur die erste Kategorie mit Files (typisch 00_Datenraum-Index)
  const [openKats, setOpenKats] = useState(null);
  useEffect(() => {
    if (groupedSorted.length > 0 && openKats === null) {
      const firstWithFiles = groupedSorted.find((g) => g.files.length > 0);
      setOpenKats(new Set(firstWithFiles ? [firstWithFiles.kat] : []));
    }
  }, [groupedSorted, openKats]);
  const toggle = (k) => {
    setOpenKats((prev) => {
      const next = new Set(prev || []);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const totalSize = useMemo(() => (data?.files || []).reduce((s, f) => s + (f.size_bytes || 0), 0), [data]);
  const HARD_LIMIT = 1.5 * 1024 * 1024 * 1024;
  const allTooBig = totalSize > HARD_LIMIT;

  return (
    <div className="min-h-screen bg-paper text-navy" data-testid="datenraum-investor-page">
      {/* Header */}
      <header className="border-b border-navy/15 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="pb-eyebrow flex items-center gap-2">
              <Vault size={11} /> Newton Campus Garching
            </div>
            <h1 className="font-display font-bold text-3xl text-navy tracking-tight mt-1">
              Datenraum
            </h1>
            {data?.investor?.firma && (
              <p className="text-sm font-sans text-navy/75 mt-2">
                Zugang für <span className="font-medium text-navy">{data.investor.firma}</span>
              </p>
            )}
          </div>
          <div className="text-right">
            <div className="pb-eyebrow">Adldinger Unternehmensgruppe</div>
            <div className="font-display font-semibold text-gold text-lg leading-none mt-1">Adldinger</div>
          </div>
        </div>
      </header>

      {/* Hinweis-Banner */}
      {!error && (
        <div className="bg-gold/10 border-b border-gold/30">
          <div className="max-w-5xl mx-auto px-6 py-3 text-xs font-sans text-navy/75 flex items-start gap-2">
            <Shield size={12} className="text-gold mt-0.5 shrink-0" />
            <span>
              <span className="font-medium text-navy">Vertraulich.</span> Diese Unterlagen sind ausschließlich
              für den genannten Empfänger bestimmt. Eine Weitergabe an Dritte ist ohne ausdrückliche
              Zustimmung der Adldinger Unternehmensgruppe nicht zulässig. Zugriffe werden protokolliert.
            </span>
          </div>
        </div>
      )}

      <main className="max-w-5xl mx-auto px-6 py-10">
        {loading && (
          <div className="text-center text-navy/55 font-sans py-16">Lade Datenraum…</div>
        )}

        {error && (
          <div className="bg-tot/10 border border-tot/40 p-8 text-center" data-testid="datenraum-error">
            <AlertTriangle size={32} className="text-tot mx-auto mb-3" />
            <div className="text-tot font-display font-semibold text-xl mb-2">Zugriff nicht möglich</div>
            <div className="text-sm font-sans text-navy/65">{error}</div>
            <div className="text-xs font-sans text-navy/45 mt-4">
              Bei Fragen wenden Sie sich bitte direkt an Ihren Adldinger-Kontakt.
            </div>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Meta + Komplett-Download */}
            <div className="mb-6 flex flex-wrap items-center gap-4">
              <div className="flex flex-wrap items-center gap-3 text-xs font-sans text-navy/65">
                <span className="inline-flex items-center gap-1.5">
                  <Clock size={11} /> Zugang gültig bis {data.ablauf_datum}
                </span>
                <span className="text-navy/30">·</span>
                <span data-testid="datenraum-file-count" className="tabular-nums">
                  <strong className="text-navy">{data.total_files}</strong> Dokument{data.total_files === 1 ? "" : "e"}
                </span>
                <span className="text-navy/30">·</span>
                <span className="tabular-nums">{fmtSize(totalSize)}</span>
              </div>
              {data.total_files > 0 && (
                <button
                  onClick={() => !allTooBig && triggerZip(drAllZipUrl(token))}
                  disabled={allTooBig}
                  data-testid="dri-download-all-btn"
                  className={
                    "ml-auto inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widish font-semibold " +
                    (allTooBig
                      ? "bg-navy/15 text-navy/50 cursor-not-allowed"
                      : "bg-navy text-paper hover:bg-navy/85 transition-colors")
                  }
                  title={allTooBig ? `Über 1.5 GB — bitte einzelne Kategorien laden` : `Alle ${data.total_files} Dateien als ZIP (${fmtSize(totalSize)})`}
                >
                  <Package size={13} /> Alles als ZIP ({fmtSize(totalSize)})
                </button>
              )}
            </div>

            {data.total_files === 0 ? (
              <div className="bg-white border border-navy/15 p-10 text-center text-navy/55 font-sans text-sm">
                Aktuell sind noch keine Dokumente für Sie freigegeben.
              </div>
            ) : (
              <div className="space-y-3" data-testid="dri-accordion">
                {groupedSorted.map(({ kat, files, meta }) => {
                  const isOpen = openKats?.has(kat);
                  const Icon = KATEGORIE_ICON[kat] || Files;
                  const label = meta?.label || kat;
                  const visKey = meta?.default_visibility || "sofort";
                  const visCfg = VISIBILITY_LABEL[visKey] || VISIBILITY_LABEL.sofort;
                  const catSize = files.reduce((s, f) => s + (f.size_bytes || 0), 0);
                  const isLocked = files.length === 0 && visKey !== "sofort";
                  return (
                    <section
                      key={kat}
                      className={"bg-white border " + (isLocked ? "border-navy/10" : "border-navy/15")}
                      data-testid={`dri-section-${kat}`}
                    >
                      <header className="flex items-stretch">
                        <button
                          onClick={() => !isLocked && toggle(kat)}
                          data-testid={`dri-toggle-${kat}`}
                          aria-expanded={isOpen}
                          disabled={isLocked}
                          className={
                            "flex-1 flex items-center gap-3 px-5 py-3.5 text-left transition-colors min-w-0 " +
                            (isLocked ? "cursor-not-allowed" : "hover:bg-ledger/50")
                          }
                        >
                          {isLocked ? (
                            <Lock size={14} className="text-navy/30 shrink-0" />
                          ) : (
                            <ChevronDown
                              size={16}
                              className={"text-navy/65 shrink-0 transition-transform " + (isOpen ? "rotate-0" : "-rotate-90")}
                            />
                          )}
                          <Icon size={16} className={isLocked ? "text-navy/30 shrink-0" : "text-gold shrink-0"} />
                          <h2 className={"font-display font-semibold text-base sm:text-lg tracking-tight min-w-0 truncate " + (isLocked ? "text-navy/40" : "text-navy")}>
                            {label}
                          </h2>
                          {files.length > 0 ? (
                            <span className="text-xs text-navy/55 font-sans tabular-nums whitespace-nowrap">
                              · {files.length} Datei{files.length === 1 ? "" : "en"} · {fmtSize(catSize)}
                            </span>
                          ) : (
                            <span className="text-xs text-navy/40 font-sans italic whitespace-nowrap">
                              · gesperrt
                            </span>
                          )}
                          <span className={`hidden sm:inline-block ml-2 text-[10px] uppercase tracking-widish border px-1.5 py-0.5 font-sans ${visCfg.bg}`}>
                            {visCfg.de}
                          </span>
                        </button>
                        {files.length > 0 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); triggerZip(drCategoryZipUrl(token, kat)); }}
                            data-testid={`dri-cat-zip-${kat}`}
                            className="flex items-center gap-1.5 px-3 sm:px-4 border-l border-navy/15 text-xs uppercase tracking-widish font-sans font-medium text-navy hover:text-paper hover:bg-navy transition-colors whitespace-nowrap"
                            title={`Alle ${files.length} Dateien als ZIP (${fmtSize(catSize)})`}
                          >
                            <Package size={13} /> <span className="hidden sm:inline">ZIP</span>
                          </button>
                        )}
                      </header>

                      {isLocked && (
                        <div className="border-t border-navy/10 px-5 py-3 text-xs font-sans text-navy/55 flex items-start gap-2 bg-ledger/30">
                          <Lock size={11} className="mt-0.5 shrink-0" />
                          <span>
                            {visKey === "nach_NDA"
                              ? "Diese Kategorie wird nach Unterzeichnung der Vertraulichkeitsvereinbarung (NDA) freigegeben."
                              : visKey === "nach_indikativ"
                              ? "Diese Kategorie wird nach Abgabe Ihres indikativen Gebots freigegeben."
                              : "Aktuell nicht freigegeben."}
                          </span>
                        </div>
                      )}

                      {!isLocked && isOpen && files.length > 0 && (
                        <ul className="border-t border-navy/10" data-testid={`dri-list-${kat}`}>
                          {files.map((f) => (
                            <li
                              key={f.id}
                              data-testid={`datenraum-file-${f.id}`}
                              className="px-5 py-3 flex items-center justify-between gap-4 border-b border-navy/5 last:border-b-0 hover:bg-ledger/40 transition-colors"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="font-sans text-navy text-sm break-words">{f.filename_original}</div>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-[11px] font-sans text-navy/55">
                                  <span className="tabular-nums">{fmtSize(f.size_bytes)}</span>
                                  {f.version && <span className="text-gold uppercase tracking-wider">{f.version}</span>}
                                </div>
                                {f.beschreibung && (
                                  <div className="text-[11px] font-sans text-navy/55 mt-1 break-words">{f.beschreibung}</div>
                                )}
                              </div>
                              <a
                                href={drInvestorDownloadUrl(token, f.id)}
                                download={f.filename_original}
                                data-testid={`datenraum-download-${f.id}`}
                                className="pb-btn-primary gap-2 shrink-0 text-xs uppercase tracking-widish font-semibold min-h-[40px]"
                              >
                                <Download size={13} /> <span className="hidden sm:inline">Laden</span>
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-navy/15 bg-white mt-12">
        <div className="max-w-5xl mx-auto px-6 py-6 text-xs font-sans text-navy/55 flex flex-wrap justify-between gap-4">
          <div>
            <div className="font-medium text-navy">Adldinger Unternehmensgruppe</div>
            <div>Investoren-Akquise Newton Campus Garching</div>
          </div>
          <div className="text-right">
            <div>Stand: {new Date().toLocaleDateString("de-DE")}</div>
            <div className="text-navy/45 mt-0.5">Dieser Zugang wird protokolliert.</div>
          </div>
        </div>
      </footer>
      {bulkModal}
    </div>
  );
}
